import { useMemo, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, GitFork, Loader2, UserRound, Users } from 'lucide-react';

import { getPatientFullName } from '@/api/mappers';
import { Button } from '@/components/ui/button';
import { useSetBreadcrumbs, type BreadcrumbItem } from '@/components/shell/ShellContext';
import { personPedigreesQuery } from '@/features/pedigree/personPedigrees';
import type { PatientViewModel } from '@/models/patient';

interface NonPatientRecordModalProps {
  readonly patient: PatientViewModel;
}

/** A distinct family the individual belongs to. */
interface FamilyLink {
  readonly familyGuid: string;
  readonly familyName: string | null;
}

/** A pedigree the individual is a node in. */
interface PedigreeLink {
  readonly pedigreeGuid: string;
  readonly label: string;
  readonly familyName: string | null;
}

/**
 * Full-content redirect screen shown when a record URL resolves to a NON-patient individual
 * (`personType === 'FAMILY_MEMBER'`) reached directly by URL (PTV2-1157). An individual has no
 * patient record of its own — it exists only as a family member and/or a node in one or more
 * pedigrees — so instead of the patient record we cover the page (below the app top bar) with a panel
 * that routes the user to where the individual actually lives:
 *
 *  - **View in family** → the family record with the member's row scrolled-to + highlighted
 *    (`/families/{familyGuid}?member={personGuid}#members`).
 *  - **Open in pedigree** → the pedigree editor, centered on + selecting the individual's node
 *    (`/patients/{personGuid}/pedigree?pedigree={pedigreeGuid}&selectPerson={personGuid}`).
 *
 * When the individual is in no family and no pedigree, an explanatory message + Back is shown.
 */
export function NonPatientRecordModal({ patient }: NonPatientRecordModalProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const personGuid = patient.guid;
  const fullName = getPatientFullName(patient) ?? t('patient.header.unknownName', { defaultValue: 'Unknown' });

  const trail = useMemo<readonly BreadcrumbItem[]>(
    () => [{ label: t('shell.breadcrumbs.worklist', { defaultValue: 'Worklist' }), to: '/browse/patients' }, { label: fullName }],
    [t, fullName],
  );
  useSetBreadcrumbs(trail);

  const query = useQuery(personPedigreesQuery(personGuid));
  const rows = useMemo(() => query.data ?? [], [query.data]);

  // Distinct families (a person appears once per pedigree; collapse to one entry per family).
  const families = useMemo<readonly FamilyLink[]>(() => {
    const byGuid = new Map<string, FamilyLink>();
    for (const r of rows) {
      if (r.family_guid !== undefined && r.family_guid !== null && r.family_guid.length > 0 && !byGuid.has(r.family_guid)) {
        byGuid.set(r.family_guid, { familyGuid: r.family_guid, familyName: r.family_name ?? null });
      }
    }
    return [...byGuid.values()];
  }, [rows]);

  const pedigrees = useMemo<readonly PedigreeLink[]>(
    () =>
      rows.map((r) => ({
        pedigreeGuid: r.pedigree_guid,
        label:
          r.pedigree_name && r.pedigree_name.length > 0
            ? r.pedigree_name
            : r.family_name && r.family_name.length > 0
              ? r.family_name
              : r.proband_name && r.proband_name.length > 0
                ? t('patient.nonPatient.pedigreeOfProband', { defaultValue: '{{name}} pedigree', name: r.proband_name })
                : t('patient.nonPatient.untitledPedigree', { defaultValue: 'Untitled pedigree' }),
        familyName: r.family_name ?? null,
      })),
    [rows, t],
  );

  const openFamily = (familyGuid: string): void => {
    void navigate(`/families/${encodeURIComponent(familyGuid)}?member=${encodeURIComponent(personGuid)}#members`);
  };

  const openPedigree = (pedigreeGuid: string): void => {
    const returnTo = encodeURIComponent(`/patients/${personGuid}`);
    void navigate(
      `/patients/${encodeURIComponent(personGuid)}/pedigree?pedigree=${encodeURIComponent(pedigreeGuid)}` +
        `&selectPerson=${encodeURIComponent(personGuid)}&returnTo=${returnTo}`,
    );
  };

  const isLoading = query.isPending;
  const isEmpty = !isLoading && families.length === 0 && pedigrees.length === 0;

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-background/90 p-6">
      <div className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-lg">
        <div className="flex items-start gap-3 border-b border-border px-6 py-5">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <UserRound className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-foreground">{fullName}</h2>
            <p className="text-sm text-muted-foreground">
              {t('patient.nonPatient.subtitle', {
                defaultValue: 'This is a family member, not a patient — they have no patient record of their own.',
              })}
            </p>
          </div>
        </div>

        <div className="px-6 py-5">
          {isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('patient.nonPatient.loading', { defaultValue: 'Finding where this member appears…' })}
            </p>
          ) : isEmpty ? (
            <p className="text-sm text-muted-foreground">
              {t('patient.nonPatient.empty', {
                defaultValue:
                  'This family member is not linked to any family or pedigree yet, so there is nothing to open.',
              })}
            </p>
          ) : (
            <div className="space-y-6">
              {families.length > 0 && (
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('patient.nonPatient.familiesHeading', { defaultValue: 'Families' })}
                  </h3>
                  <ul className="space-y-2">
                    {families.map((f) => (
                      <li key={f.familyGuid}>
                        <button
                          type="button"
                          onClick={() => {
                            openFamily(f.familyGuid);
                          }}
                          className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:border-primary/50 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <Users className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                            <span className="truncate text-sm font-medium text-foreground">
                              {f.familyName && f.familyName.length > 0
                                ? f.familyName
                                : t('patient.nonPatient.unnamedFamily', { defaultValue: 'Unnamed family' })}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1 text-xs text-primary">
                            {t('patient.nonPatient.viewInFamily', { defaultValue: 'View member' })}
                            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {pedigrees.length > 0 && (
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('patient.nonPatient.pedigreesHeading', { defaultValue: 'Pedigrees' })}
                  </h3>
                  <ul className="space-y-2">
                    {pedigrees.map((p) => (
                      <li key={p.pedigreeGuid}>
                        <button
                          type="button"
                          onClick={() => {
                            openPedigree(p.pedigreeGuid);
                          }}
                          className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:border-primary/50 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <GitFork className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                            <span className="truncate text-sm font-medium text-foreground">{p.label}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1 text-xs text-primary">
                            {t('patient.nonPatient.openInPedigree', { defaultValue: 'Open & select' })}
                            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-border px-6 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // `key === 'default'` means this is the first entry in the session (arrived by direct
              // URL, no in-app history) — Back has nowhere to go, so send them to the root.
              if (location.key === 'default') {
                void navigate('/');
              } else {
                void navigate(-1);
              }
            }}
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t('common.back', { defaultValue: 'Back' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
